import {
  DateRange,
  FilterTimeRange,
  Shift,
  SHIFT_QUICK_SELECT_SLOT_LIST,
  SHIFT_QUICK_SELECT_SLOTS,
  ShiftNotFoundError,
  ShiftQuickSelectSlots,
  TimeRange
} from '@hconnect/common/types'
import {getQuickSelectFilter, generateTimeRange, toTimeRange} from '@hconnect/common/utils'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {isSingleShiftRange} from '@widgets/shift-event-details'
import moment from 'moment'
import {useEffect, useMemo, useState} from 'react'

import {useTimeRange} from './useTimeRange'

export const useFilterDatePicker = (plantId: string, shifts: Shift[]) => {
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)
  const filterTimeRange = useTimeRange(plantId)
  const quickSelectSlot =
    (SHIFT_QUICK_SELECT_SLOT_LIST.includes(filterTimeRange as SHIFT_QUICK_SELECT_SLOTS) &&
      (filterTimeRange as ShiftQuickSelectSlots)) ||
    'currentShift'
  const [start, end] = getQuickSelectFilter(quickSelectSlot, shifts, getPlantNow())
  if (!start || !end) {
    throw new ShiftNotFoundError()
  }
  const [timeRange, setTimeRange] = useState<TimeRange>(toTimeRange([start, end]))
  const [isSingleShift, setShiftRange] = useState<boolean>(
    isSingleShiftRange(
      moment.utc(timeRange.startDate).tz(timezone),
      moment.utc(timeRange.endDate).tz(timezone),
      shifts
    )
  )
  const dateRange = useMemo<DateRange>(
    () => ({
      startDate: moment.utc(timeRange.startDate).tz(timezone),
      endDate: moment.utc(timeRange.endDate).tz(timezone)
    }),
    [timeRange.endDate, timeRange.startDate, timezone]
  )

  const getDateRange = (): DateRange => {
    const [startDate, endDate] = getQuickSelectFilter('currentShift', shifts, getPlantNow())
    if (!startDate || !endDate) {
      throw new ShiftNotFoundError()
    }
    return {startDate, endDate}
  }

  useEffect(() => {
    const isSingleShift = isSingleShiftRange(
      moment.utc(timeRange.startDate).tz(timezone),
      moment.utc(timeRange.endDate).tz(timezone),
      shifts
    )
    setShiftRange(isSingleShift)
  }, [timeRange, shifts, timezone])

  useEffect(() => {
    const filterRange: FilterTimeRange = generateTimeRange(filterTimeRange, shifts, getPlantNow)
    if (filterRange.startDate && filterRange.endDate) {
      const range: TimeRange = {
        startDate: filterRange.startDate,
        endDate: filterRange.endDate
      }
      setTimeRange(range)
    }
  }, [filterTimeRange, timezone, shifts, getPlantNow])

  return {
    timeRange,
    dateRange,
    isSingleShift,
    getDateRange,
    quickSelectSlot
  }
}
